/**
 * /stats — the numbers on a page the founder can open on a phone (Sprint 1 item 5).
 *
 * Mounted by src/index.tsx instead of App, so there is no Carousel, no TelemetryTransport and
 * no feed resource: reading the numbers never starts a card session on card 0, which every
 * read from the profile sheet does today. This file imports nothing that can emit a row — not
 * App, the telemetry modules, api/ui, lib/share or lib/visit — and tests/unit/stats.test.ts
 * pins that list.
 *
 * Who is looking is PEEKED, never minted: the account API below is built on a resolver that
 * only reads auth.getSession(), so a stranger who opens a page of numbers does not become an
 * auth user and an actor row (stats_totals.actors would count them). Only the internal button
 * asks for a session, because the flag it sets is what an anonymous tour browser came for.
 * The client's isAdmin only chooses which door to show; every reader raises 42501 on its own
 * (migration 0014's pattern) and that sentence is shown verbatim if it ever arrives.
 *
 * card_rank is callable by anon in the database; keeping it behind this door is a product
 * choice. retention_daily's d1 is null for today and yesterday by design (0019), which the
 * legend says rather than printing 0. The 14-day window is the sprint's read; the feed itself
 * orders on card_rank(28), and the Deepstate line says so.
 */
import { For, Match, Show, Switch, createResource, createSignal, onMount } from 'solid-js';
import { AccountSheet, type AccountView } from './AccountSheet';
import { ANONYMOUS, NO_BACKEND, createAccountApi, type Account, type AccountClient } from '../api/account';
import { ADMINS_ONLY, stats } from '../api/stats';
import { supabase, supabaseConfigured } from '../backend/supabase';
import {
  BASELINE_HEAD,
  BASELINE_HEAD_PER_DAY,
  NOTE_RANK,
  RANK_HEAD,
  VIAS,
  answerFor,
  baselineTaken,
  benchLine,
  dailyTable,
  deepstateLine,
  loopCall,
  loopTable,
  rankTable,
  retentionCall,
  retentionTable,
  statsGate,
  totalsLine,
  viaArg,
  type Cell,
  type MetricTable,
  type RetentionAnswer,
  type Via,
} from '../lib/statsView';

const DAYS = 14;
const WINDOW_DAYS = 14;

/** The session as it is, never minted: App's resolver signs in anonymously; this one only looks. */
const peekSession = async () => (supabase ? (await supabase.auth.getSession()).data.session : null);
const acctApi = createAccountApi(supabase as unknown as AccountClient | null, peekSession);

/**
 * The one thing the page remembers: that this browser pressed the button, and as which auth
 * user. my_profile does not report is_internal, so this is the only way the founder can tell a
 * marked tour browser from an unmarked one. Keyed to the uid so a sign-out (a new uid) stops it
 * applying, and the copy says the server does not report the flag back.
 */
const MEMO_KEY = 'blitz.internal';
interface InternalMemo {
  uid: string;
  at: string;
}
function readMemo(): InternalMemo | null {
  try {
    const raw = localStorage.getItem(MEMO_KEY);
    return raw ? (JSON.parse(raw) as InternalMemo) : null;
  } catch {
    return null;
  }
}
function writeMemo(m: InternalMemo): void {
  try {
    localStorage.setItem(MEMO_KEY, JSON.stringify(m));
  } catch {
    /* the press still happened; only the reminder is lost */
  }
}

function CellView(props: { cell: Cell }) {
  return (
    <>
      <span>{props.cell.text}</span>
      <Show when={props.cell.n}>{(n) => <span class="n">n {n()}</span>}</Show>
    </>
  );
}

/** Metrics as rows: label · baseline · days newest-first. The only thing that touches table markup. */
function MetricsTable(props: { table: MetricTable; testid: string; baselineHead: string }) {
  return (
    <>
      <div class="stats-wrap">
        <table class="stats stats-metrics" data-testid={props.testid}>
          <thead>
            <tr>
              <th scope="col"></th>
              <th scope="col" class="base">
                {props.baselineHead}
              </th>
              <For each={props.table.days}>{(d) => <th scope="col">{d}</th>}</For>
            </tr>
          </thead>
          <tbody>
            <For each={props.table.rows}>
              {(r) => (
                <tr data-metric={r.key}>
                  <th scope="row">{r.key}</th>
                  <td class="base" classList={{ pending: r.baseline.state === 'pending', none: r.baseline.state === 'none', na: r.baseline.state === 'na' }}>
                    <CellView cell={r.baseline} />
                  </td>
                  <For each={r.cells}>
                    {(c) => (
                      <td classList={{ pending: c.state === 'pending', none: c.state === 'none', na: c.state === 'na' }}>
                        <CellView cell={c} />
                      </td>
                    )}
                  </For>
                </tr>
              )}
            </For>
          </tbody>
        </table>
      </div>
      <For each={props.table.notes}>{(t) => <p class="stats-note">{t}</p>}</For>
    </>
  );
}

export function StatsPage() {
  const [acct, setAcct] = createSignal<Account>(supabaseConfigured ? ANONYMOUS : NO_BACKEND);
  // False until the first peek answers, so the door does not flash 'Sign in' at an admin.
  const [known, setKnown] = createSignal(!supabaseConfigured);
  const [uid, setUid] = createSignal<string | null>(null);
  const [sheetOpen, setSheetOpen] = createSignal(false);
  const [sheetView, setSheetView] = createSignal<AccountView>('signin');
  const [toast, setToast] = createSignal<string | null>(null);
  const [via, setVia] = createSignal<Via>('all');
  const [busy, setBusy] = createSignal(false);
  const [internalError, setInternalError] = createSignal<string | null>(null);
  const [memo, setMemo] = createSignal<InternalMemo | null>(readMemo());
  const gate = () => statsGate(acct());

  /** Peek, never mint: acctApi's resolver only reads the session that is there. */
  const readAccount = async (): Promise<Account> => {
    const s = await peekSession();
    setUid(s?.user.id ?? null);
    return acctApi.load();
  };
  /** The sheet signed someone in or out (through the app's own account API): the uid changed with it. */
  const onAccount = (a: Account) => {
    setAcct(a);
    void peekSession().then((s) => setUid(s?.user.id ?? null));
  };

  let toastTimer = 0;
  const showToast = (message: string) => {
    setToast(message);
    clearTimeout(toastTimer);
    toastTimer = window.setTimeout(() => setToast(null), 2600);
  };
  const openSheet = (view: AccountView) => {
    setSheetView(view);
    setSheetOpen(true);
  };
  /** Profile for an account, confirm while an email is pending, else sign in: nobody comes to /stats to create an account. */
  const accountEntry = (): AccountView => (acct().isAccount ? 'profile' : acct().pendingEmail ? 'confirm' : 'signin');

  onMount(() => {
    document.title = 'BLITZ · stats';
    void readAccount().then((a) => {
      setAcct(a);
      setKnown(true);
    });
  });

  // The four readers that do not depend on the door filter, in one fetch. A null source means
  // "not an admin, ask nothing" — the server would refuse anyway; there is no need to try.
  const [numbers, { refetch: refetchNumbers }] = createResource(
    () => (gate() === 'admin' ? DAYS : null),
    async (days) => {
      const [daily, totals, baseline, rank, loop, bench] = await Promise.all([
        stats.daily(days),
        stats.totals(),
        stats.baseline(),
        stats.cardRank(WINDOW_DAYS),
        stats.loopFunnel(days),
        stats.benchSummary(),
      ]);
      return { daily, totals, baseline, rank, loop, bench };
    },
  );
  // The door travels with the answer: Solid keeps the previous rows while a new source loads,
  // and those rows are the previous door's.
  const [retention, { refetch: refetchRetention }] = createResource(
    () => (gate() === 'admin' ? via() : null),
    (v): Promise<RetentionAnswer> => stats.retention(DAYS, viaArg(v)).then((r) => ({ via: v, r })),
  );
  // Null while a door switch is in flight, so the section reads 'Reading…' rather than the old
  // door's numbers under the new door's heading; a Read again keeps the table up (same door).
  const ready = () => {
    const n = numbers();
    const a = answerFor(retention(), via());
    return n && a ? { n, via: a.via, r: a.r } : null;
  };
  const taken = () => {
    const n = numbers();
    return n && n.baseline.ok ? baselineTaken(n.baseline.rows) : null;
  };
  const readAgain = () => {
    void refetchNumbers();
    void refetchRetention();
  };

  const markedHere = () => {
    const m = memo();
    return m && m.uid === uid() ? m : null;
  };
  const markInternal = async () => {
    // Irreversible from here (set_internal(false) has no button): ask first, as Take down does.
    if (!window.confirm('Mark this browser internal? Its sessions stop counting in every number. Undo is a line of SQL.')) return;
    setBusy(true);
    setInternalError(null);
    const r = await stats.markInternal();
    setBusy(false);
    if (!r.ok) {
      setInternalError(r.error);
      return;
    }
    const m = { uid: r.uid, at: new Date().toISOString() };
    writeMemo(m);
    setMemo(m);
    setUid(r.uid);
    showToast('Marked internal');
    // The flag is read at query time, so every number on screen is stale: read them again.
    if (gate() === 'admin') readAgain();
  };
  const internalHint = () => {
    const a = acct();
    if (!a.isAccount) return 'Marks this browser’s profile internal: its sessions stop counting in DAU, retention and votes. For tour browsers that are not signed in as @rand.';
    return a.handle
      ? `Signed in as @${a.handle}: this marks the account internal, on every device signed in as it.`
      : 'Signed in: this marks your account internal, on every device signed in as it.';
  };

  return (
    <div class="app">
      <header class="top-bar">
        {/* The mark is the way to the landing page (the site root) — the one place that explains what this is. */}
        <a class="brand" data-ui href="/" data-testid="brand-link" aria-label="About Blitz">
          <img src="/icons/icon.svg" alt="" />
          BLITZ
        </a>
        <div class="top-actions">
          <button
            class="icon-btn who"
            classList={{ signed: acct().isAccount }}
            data-ui
            data-testid="account"
            aria-label={acct().handle ? `Your account, @${acct().handle}` : 'Account'}
            title={acct().handle ? `@${acct().handle}` : 'Sign in'}
            onClick={() => openSheet(accountEntry())}
          >
            <Show
              when={acct().handle}
              fallback={
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <circle cx="12" cy="8" r="4" />
                  <path d="M4 21c0-4 3.6-7 8-7s8 3 8 7" />
                </svg>
              }
            >
              {acct().handle![0].toUpperCase()}
            </Show>
          </button>
        </div>
      </header>

      <main class="stats-page" data-testid="stats-page">
        <Show when={known()} fallback={<p class="stats-note">Reading who this is…</p>}>
          <Switch>
            <Match when={gate() !== 'admin'}>
              <section class="stats-door" data-testid="stats-door">
                <h2>{ADMINS_ONLY}</h2>
                <Switch>
                  <Match when={gate() === 'no-backend'}>
                    <p>This build has no backend, so there is nothing to sign in to.</p>
                  </Match>
                  <Match when={gate() === 'signin'}>
                    <p>The numbers are for the founder. Sign in as an admin to read them.</p>
                    <button type="button" class="btn primary tall" data-testid="stats-signin" onClick={() => openSheet('signin')}>
                      Sign in
                    </button>
                  </Match>
                  <Match when={gate() === 'not-admin'}>
                    <p>{acct().handle ? `Signed in as @${acct().handle} — not an admin.` : 'Signed in — not an admin.'}</p>
                    <button type="button" class="btn tall" data-testid="stats-switch" onClick={() => openSheet('profile')}>
                      Switch account
                    </button>
                  </Match>
                </Switch>
              </section>
            </Match>
            <Match when={gate() === 'admin'}>
              <section>
                <div class="stats-head">
                  <h4>stats_daily({DAYS}) · stats_totals()</h4>
                  <button type="button" class="btn tall" data-testid="stats-refresh" onClick={readAgain}>
                    Read again
                  </button>
                </div>
                {/* keyed: the callback re-runs on every refetch (a marked actor drops out of the numbers at once). */}
                <Show keyed when={numbers()} fallback={<p class="stats-note">Reading…</p>}>
                  {(n) => (
                    <>
                      {n.totals.ok ? (
                        <p class="totals">
                          <For each={totalsLine(n.totals.rows)}>
                            {(i) => (
                              <span>
                                <b>{i.value}</b> {i.label}
                              </span>
                            )}
                          </For>
                        </p>
                      ) : (
                        <p class="error" role="alert">
                          {n.totals.error}
                        </p>
                      )}
                      {n.daily.ok ? (
                        <MetricsTable testid="stats-daily" baselineHead={BASELINE_HEAD_PER_DAY} table={dailyTable(n.daily.rows, n.baseline)} />
                      ) : (
                        <p class="error" role="alert" data-testid="stats-error">
                          {n.daily.error}
                        </p>
                      )}
                    </>
                  )}
                </Show>
              </section>

              <section>
                <h4>{retentionCall(DAYS, via())}</h4>
                <div class="stats-via" role="group" aria-label="Arrived through" data-testid="stats-via">
                  <For each={VIAS}>
                    {(v) => (
                      <button type="button" aria-pressed={via() === v} data-testid={`stats-via-${v}`} onClick={() => setVia(v)}>
                        {v}
                      </button>
                    )}
                  </For>
                </div>
                <Show keyed when={ready()} fallback={<p class="stats-note">Reading…</p>}>
                  {({ n, via: door, r }) =>
                    r.ok ? (
                      <MetricsTable testid="stats-retention" baselineHead={BASELINE_HEAD} table={retentionTable(r.rows, n.baseline, door)} />
                    ) : (
                      <p class="error" role="alert" data-testid="stats-error">
                        {r.error}
                      </p>
                    )
                  }
                </Show>
                <Show when={taken()}>{(t) => <p class="stats-note">Baseline row {t()} UTC.</p>}</Show>
              </section>

              {/* The creation loop (Sprint 2): the funnel a maker walks, beside the bench it waits on.
                  Each half says its own error: before migration 0021 is applied the funnel has
                  no reader and the bench still answers. */}
              <section>
                <h4>{loopCall(DAYS)}</h4>
                <Show keyed when={numbers()} fallback={<p class="stats-note">Reading…</p>}>
                  {(n) => (
                    <>
                      {n.bench.ok ? (
                        <p class="totals" data-testid="stats-bench">
                          <For each={benchLine(n.bench.rows)}>
                            {(i) => (
                              <span>
                                <b>{i.value}</b> {i.label}
                              </span>
                            )}
                          </For>
                        </p>
                      ) : (
                        <p class="error" role="alert">
                          {n.bench.error}
                        </p>
                      )}
                      {n.loop.ok ? (
                        <MetricsTable testid="stats-loop" baselineHead={`${DAYS} d`} table={loopTable(n.loop.rows)} />
                      ) : (
                        <p class="error" role="alert" data-testid="stats-error">
                          {n.loop.error}
                        </p>
                      )}
                    </>
                  )}
                </Show>
              </section>

              <section>
                <h4>card_rank({WINDOW_DAYS}) · live cards</h4>
                <Show keyed when={numbers()} fallback={<p class="stats-note">Reading…</p>}>
                  {(n) =>
                    n.rank.ok ? (
                      <>
                        <p class="stats-line" data-testid="stats-deepstate">
                          {deepstateLine(n.rank.rows, WINDOW_DAYS)}
                        </p>
                        <div class="stats-wrap">
                          <table class="stats" data-testid="stats-rank">
                            <thead>
                              <tr>
                                <For each={RANK_HEAD}>{(h) => <th>{h}</th>}</For>
                              </tr>
                            </thead>
                            <tbody>
                              <For each={rankTable(n.rank.rows)}>
                                {(row) => (
                                  <tr>
                                    <For each={row}>{(c) => <td>{c}</td>}</For>
                                  </tr>
                                )}
                              </For>
                            </tbody>
                          </table>
                        </div>
                        <p class="stats-note">{NOTE_RANK}</p>
                      </>
                    ) : (
                      <p class="error" role="alert" data-testid="stats-error">
                        {n.rank.error}
                      </p>
                    )
                  }
                </Show>
              </section>
            </Match>
          </Switch>
        </Show>

        {/* Outside the admin door on purpose: the anonymous tour browsers this is for are never
            admins (SPRINT.md item 9), and set_internal is self-only — it can only remove the
            caller's own sessions from the numbers (20260908120000_card_rank.sql:57-71). Absent
            without a backend: there is nothing to mark. */}
        <Show when={acct().configured}>
          <section class="stats-internal" data-testid="stats-internal-block">
            <h4>set_internal(true)</h4>
            <p class="stats-note">{internalHint()}</p>
            <button type="button" class="btn tall" data-testid="stats-internal" disabled={busy()} onClick={() => void markInternal()}>
              Mark this browser internal
            </button>
            <Show when={markedHere()}>
              {(m) => (
                <p class="stats-note ok" data-testid="stats-internal-done">
                  Marked internal from this browser on {m().at.slice(0, 10)}. The server keeps the flag and does not report it back; pressing again is harmless.
                </p>
              )}
            </Show>
            <Show when={internalError()}>
              <p class="error" role="alert" data-testid="stats-internal-error">
                {internalError()}
              </p>
            </Show>
          </section>
        </Show>
      </main>

      {/* onGoTo: no carousel here to jump inside — the feed opens positioned at /c/<slug> on its
          own (src/lib/share.ts, which this file must not import). onRemoved: a take-down changes
          cardCount; MakerPanel refetches its own lists (mirrors App.tsx onRemoved). */}
      <AccountSheet
        open={sheetOpen()}
        view={sheetView()}
        account={acct()}
        onView={setSheetView}
        onClose={() => setSheetOpen(false)}
        onAccount={onAccount}
        toast={showToast}
        onGoTo={(id) => location.assign('/c/' + encodeURIComponent(id))}
        onRemoved={() => void readAccount().then(setAcct)}
      />

      <Show when={toast()}>
        <div class="toast" role="status">
          {toast()}
        </div>
      </Show>
    </div>
  );
}
